import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSlackChannels, slackUserToChannel, type SlackChannel } from "../_components/form-fields/slackChannels";
import { fetchSlackUsers } from "../_components/form-fields/slackUsers";

/**
 * Fetches Slack channels + users for a connector and returns a combined Map
 * keyed by ID. Used by read-only label-resolution call sites (run history,
 * node summary, JSON views, trigger configs) so saved targets render as
 * `#channel-name` / `@user-name` instead of opaque IDs.
 *
 * Includes private channels — these read-only consumers show what the user
 * already selected; the selector dropdown uses its own fetch to exclude
 * private channels from the discovery surface for privacy.
 */
export function useSlackChannels(connectorId: string | undefined, connectorType: string | undefined, enabled: boolean) {
  const isSlack = !!connectorId && !!connectorType && connectorType.includes("slack");
  const shouldFetch = isSlack && enabled;

  const { data: slackChannels = [] } = useQuery<SlackChannel[]>({
    queryKey: ["slack-channels", connectorId, connectorType],
    queryFn: () => fetchSlackChannels(connectorId!, connectorType!, { includePrivate: true }),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
  });

  const { data: slackUsers = [] } = useQuery<SlackChannel[]>({
    queryKey: ["slack-users-as-channels", connectorId, connectorType],
    queryFn: async () => {
      const users = await fetchSlackUsers(connectorId!, connectorType!);
      return users.map(slackUserToChannel).filter((c): c is SlackChannel => c !== null);
    },
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
  });

  const slackChannelsMap = useMemo(() => {
    const map = new Map<string, SlackChannel>();
    for (const ch of slackChannels) map.set(ch.id, ch);
    // Users are added last — a genuine channel ID collision is impossible
    // (Slack uses disjoint prefixes) but being explicit keeps the intent.
    for (const u of slackUsers) if (!map.has(u.id)) map.set(u.id, u);
    return map;
  }, [slackChannels, slackUsers]);

  return { slackChannelsMap, isSlackConnector: isSlack };
}
